/**
 * xAI (Grok) LLM Service
 * Provider-specific implementation for all 4 generation functions.
 * Uses fetch to api.x.ai/v1/chat/completions.
 */

import { HookhouseFormData, ParsedSunoOutput, MusicCritique, SunoClip } from '@/types';
import type { PersonaOverride } from '@/types/persona.types';
import { trackUsage } from '@/utils/costTracker';
import {
  getXAIKey, getXAIModel,
  parseResponse, buildSunoPromptInput, buildRegenerateInput,
  buildImagePrompt, buildCritiquePrompt, parseCritiqueResponse,
  enforceLyricsLimit,
  MUSIC_CRITIQUE_JSON_INSTRUCTION,
} from './llmShared';
import type { RegenerateOptions } from './llmShared';

interface XAIChatResponse {
  choices?: { message?: { content?: string } }[];
  usage?: { prompt_tokens?: number; completion_tokens?: number; cost_in_usd_ticks?: number };
  model?: string;
  error?: { message?: string };
}

async function callXAI(
  systemPrompt: string, userPrompt: string, temperature: number, maxTokens: number,
  jsonMode: boolean = false
): Promise<{ text: string; inputTokens: number; outputTokens: number; actualCost?: number }> {
  const apiKey = getXAIKey();
  if (!apiKey) throw new Error("xAI API key not configured. Please set your API key in Settings.");
  const model = getXAIModel();
  console.log(`[xAI] Calling API with model: ${model}`);

  const body: Record<string, unknown> = {
    model,
    temperature,
    max_tokens: maxTokens,
    messages: [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userPrompt }
    ]
  };
  if (jsonMode) body.response_format = { type: "json_object" };

  const response = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
    body: JSON.stringify(body)
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({})) as XAIChatResponse;
    throw new Error(`xAI API error: ${errorData?.error?.message || `HTTP ${response.status}`}`);
  }

  const data = await response.json() as XAIChatResponse;
  const text = data.choices?.[0]?.message?.content || '';
  const inputTokens = data.usage?.prompt_tokens || 0;
  const outputTokens = data.usage?.completion_tokens || 0;
  const costTicks = data.usage?.cost_in_usd_ticks;
  const actualCost = typeof costTicks === 'number' ? costTicks / 10_000_000_000 : undefined;

  return { text, inputTokens, outputTokens, actualCost };
}

export const generateSunoPrompt = async (
  data: HookhouseFormData,
  vocalArchitecture?: string,
  productionArchitecture?: string,
  riffDnaArchitecture?: string,
  soloArchitecture?: string,
  narrativeArchitecture?: string,
  personaOverride?: PersonaOverride | null
): Promise<ParsedSunoOutput> => {
  const input = buildSunoPromptInput(data, vocalArchitecture, productionArchitecture, riffDnaArchitecture, soloArchitecture, narrativeArchitecture, personaOverride);

  try {
    const { text, inputTokens, outputTokens, actualCost } = await callXAI(input.systemPrompt, input.userPrompt, 0.85, 8000);
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'generation', inputTokens, outputTokens, actualCost });
    if (!text) throw new Error("No response from xAI.");
    const parsed = parseResponse(text, input.styleStrength, input.weirdness);
    return await enforceLyricsLimit(parsed, callXAI);
  } catch (error: unknown) {
    console.error("xAI API Error:", error);
    throw new Error(error instanceof Error ? error.message : "Unknown error generating prompt. Check your API key.");
  }
};

export const regenerateWithAdvice = async (originalOutput: ParsedSunoOutput, critique: MusicCritique, opts?: RegenerateOptions): Promise<ParsedSunoOutput> => {
  const input = buildRegenerateInput(originalOutput, critique, opts);

  try {
    const { text, inputTokens, outputTokens, actualCost } = await callXAI(input.systemPrompt, input.userPrompt, 0.8, 8000);
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'generation', inputTokens, outputTokens, actualCost });
    if (!text) throw new Error("No response from xAI.");
    return parseResponse(text, input.styleStrength, input.weirdness);
  } catch (error: unknown) {
    throw new Error(`Regeneration Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateImageBlueprint = async (output: ParsedSunoOutput): Promise<string> => {
  const { systemPrompt, userPrompt } = buildImagePrompt(output);

  try {
    const { text, inputTokens, outputTokens, actualCost } = await callXAI(
      systemPrompt + "\nRespond in JSON only, no markdown wrapping.",
      userPrompt, 0.7, 4000, true
    );
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'artwork', inputTokens, outputTokens, actualCost });
    return text || "";
  } catch (error: unknown) {
    throw new Error(`Blueprint Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateMusicCritique = async (clip: SunoClip): Promise<MusicCritique> => {
  const { systemPrompt, userPrompt } = buildCritiquePrompt(clip);

  try {
    const { text, inputTokens, outputTokens, actualCost } = await callXAI(
      systemPrompt, userPrompt + MUSIC_CRITIQUE_JSON_INSTRUCTION, 0.75, 4000, true
    );
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'critic', inputTokens, outputTokens, actualCost });
    if (!text) throw new Error("No response from xAI for critique.");
    return parseCritiqueResponse(text, clip.id);
  } catch (error: unknown) {
    console.error("Critique Error:", error);
    throw new Error(`Critique Generation Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};
